
import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MarketplacePreview from "@/components/MarketplacePreview";
import SecurityFooter from "@/components/SecurityFooter";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { Shield, Star, MapPin, Clock, Users, CheckCircle } from "lucide-react";

const MarketplacePage = () => {
  useEffect(() => {
    // Add canonical tag for SEO
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://zimkonnect.com/marketplace');
    } else {
      const newCanonical = document.createElement('link');
      newCanonical.rel = 'canonical';
      newCanonical.href = 'https://zimkonnect.com/marketplace';
      document.head.appendChild(newCanonical);
    }

    // Update page metadata
    document.title = "Verified Suppliers Marketplace | ZimKonnect Zimbabwe";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Browse verified builders, contractors, and suppliers in Zimbabwe. All service providers vetted by ZimKonnect for diaspora project security and trust.');
    }
  }, []);

  const marketplaceFeatures = [
    {
      icon: Shield,
      title: "Verified Suppliers Only",
      description: "Every builder, contractor, and service provider in our marketplace has been thoroughly vetted by our team and community members."
    },
    {
      icon: Star,
      title: "Community Ratings",
      description: "Real reviews and ratings from diaspora community members who have used these suppliers for their projects in Zimbabwe."
    },
    {
      icon: MapPin,
      title: "Location-Based Search",
      description: "Find suppliers in specific cities and regions across Zimbabwe, from Harare and Bulawayo to rural areas."
    },
    {
      icon: Clock,
      title: "Real-Time Availability",
      description: "See which suppliers are currently available for new projects and their estimated response times."
    },
    {
      icon: Users,
      title: "Direct Communication",
      description: "Connect directly with suppliers through our secure messaging system to discuss your project requirements."
    },
    {
      icon: CheckCircle,
      title: "Project History",
      description: "View completed projects, testimonials, and work samples from each supplier to make informed decisions."
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Verified Suppliers
              <span className="block bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
                Marketplace
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Connect with trusted builders, contractors, and service providers across Zimbabwe. 
              Every supplier is verified by our community for your peace of mind.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-blue-600 to-orange-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-orange-600 transition-all duration-300">
                Browse Suppliers
              </button>
              <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all duration-300">
                Become a Supplier
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-16">Why Choose Our Marketplace</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {marketplaceFeatures.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-white shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketplacePreview />
      <Footer />
      <SecurityFooter />
      <CookieConsentBanner />
    </div>
  );
};

export default MarketplacePage;
